import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

export interface ProductOption {
  id: number;
  sku: string;
  name: string;
  price?: number;
  attributes?: Array<{ attribute_name: string; value: string }>;
}

interface ProductTypeaheadProps {
  /** Currently selected product id (as string), or '' when nothing is selected. */
  value: string;
  /** Called with the selected product (or null when the field is cleared). */
  onSelect: (product: ProductOption | null) => void;
  /**
   * Optional pre-known products (e.g. already loaded on the page). Used to
   * render the label for the current `value` without an extra fetch, and as a
   * fallback suggestion source before the user types.
   */
  knownProducts?: ProductOption[];
  /**
   * Label to display immediately for the current `value` when the product's
   * sku/name are already known by the parent (e.g. items copied from an
   * outward job order). Avoids a flash of blank text and any dependency on a
   * capped product list or a follow-up fetch to render what we already have.
   */
  initialLabel?: string;
  placeholder?: string;
  /** Build the display label for a product. Defaults to "SKU - Name". */
  labelFor?: (p: ProductOption) => string;
  style?: React.CSSProperties;
}

const defaultLabel = (p: ProductOption) => `${p.sku} - ${p.name}`;

/**
 * Server-side typeahead for selecting a product from the catalog.
 *
 * Unlike a native <datalist> populated from a capped page of products, this
 * queries /products/?q=<text> as the user types, so it can reach any product
 * in the catalog regardless of alphabetical position, variant-child status, or
 * catalog size.
 */
const ProductTypeahead: React.FC<ProductTypeaheadProps> = ({
  value,
  onSelect,
  knownProducts = [],
  initialLabel,
  placeholder = 'Type SKU or product name...',
  labelFor = defaultLabel,
  style,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inputVal, setInputVal] = useState(initialLabel || '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ProductOption[]>([]);
  const [query, setQuery] = useState('');
  // True once the user starts typing, so we don't clobber their text when a
  // late value/knownProducts sync fires.
  const editingRef = useRef(false);

  // Keep the displayed text in sync with the selected value. Re-runs when the
  // known-products list arrives (it loads async after the form opens) so a
  // programmatically pre-set value resolves its label as soon as possible.
  useEffect(() => {
    if (editingRef.current) return;

    if (!value) {
      setInputVal(initialLabel || '');
      return;
    }

    const known = knownProducts.find((p) => String(p.id) === String(value));
    if (known) {
      setInputVal(labelFor(known));
      return;
    }

    // Show the caller-supplied label right away if we have one.
    if (initialLabel) setInputVal(initialLabel);

    // Resolve the full label for a selected product that isn't in knownProducts
    // (e.g. a variant child beyond the pre-loaded page).
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/products/${value}`);
        const p = res.data;
        if (!cancelled && p && p.id && !editingRef.current) {
          setInputVal(labelFor({ id: p.id, sku: p.sku, name: p.name, price: p.price, attributes: p.attributes }));
        }
      } catch {
        /* keep initialLabel (or blank) if the product can't be loaded */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, knownProducts, initialLabel]);

  // Close dropdown on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced server-side search against the catalog.
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const res = await api.get('/products/', { params: { q: term, limit: 50 } });
        const products = Array.isArray(res.data?.products) ? res.data.products : [];
        setResults(products);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleChange = (val: string) => {
    editingRef.current = true;
    setInputVal(val);
    setQuery(val);
    setOpen(true);
    if (!val) onSelect(null);
  };

  const handlePick = (p: ProductOption) => {
    editingRef.current = false;
    setInputVal(labelFor(p));
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(p);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={inputVal}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', ...style }}
      />
      {open && query.trim() && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflow: 'auto', marginTop: '2px',
        }}>
          {loading && <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>Loading...</div>}
          {!loading && results.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>No matches</div>
          )}
          {!loading && results.map((p) => (
            <div
              key={p.id}
              onMouseDown={() => handlePick(p)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
            >
              <span style={{ fontFamily: 'monospace', color: '#64748b', fontSize: '11px' }}>{p.sku}</span>
              <span style={{ marginLeft: '8px' }}>{p.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductTypeahead;
